import { forwardRef, type ButtonHTMLAttributes } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'xs' | 'sm' | 'md' | 'lg';

// `focus:ring-*` (not `focus-visible:`) keeps the ring always visible on focus —
// the design system asks for an unmissable focus target on every control.
const base =
  'cursor-pointer inline-flex items-center justify-center gap-x-2 font-medium rounded-lg transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';

// Tap targets stay >= 40px tall on `sm` and up (py + line-height).
const sizes: Record<Size, string> = {
  // `xs` is only for the row actions of a dense table. It may sit under 40px
  // because that column is hidden below `md` (a touch layout acts through the
  // tick boxes and the batch bar), and a 40px button there sets the height of
  // every row of the table.
  xs: 'text-xs py-1 px-2.5 min-h-[32px]',
  sm: 'text-sm py-2 px-3 min-h-[40px]',
  md: 'text-sm py-2 px-4 min-h-[40px]',
  // `lg` is for the one primary action of a screen (the card scan): thumb-sized
  // on a tablet at arm's length, still 44px+ everywhere else.
  lg: 'text-base py-4 px-6 min-h-[56px]',
};

const variants: Record<Variant, string> = {
  primary:
    'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] focus:ring-[var(--color-accent)]',
  // Text on the green fill is --color-text, not white: white on #00c897 is ~2.2:1
  // and fails even the large-text bar (see docs/features/ADR-0004-design-system.md).
  secondary:
    'bg-[var(--color-secondary)] text-[var(--color-text)] hover:bg-[var(--color-secondary-hover)] focus:ring-[var(--color-secondary)]',
  ghost:
    'border border-[var(--color-border-control)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:border-[var(--color-text-secondary)] focus:ring-[var(--color-accent)]',
  danger: 'bg-[var(--color-danger)] text-white hover:opacity-90 focus:ring-[var(--color-danger)]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
}

/**
 * Brand button. Defaults to the primary (teal) variant.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', fullWidth = false, className = '', type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={[base, sizes[size], variants[variant], fullWidth ? 'w-full' : '', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
      // Declared in the DOM so a container can act on it: `Modal` refuses to let
      // Enter press a `danger` action, because a destructive step must be a
      // deliberate click, never a stray keystroke.
      data-variant={variant}
    />
  );
});
